/**
 * Battery comparison: which battery the study is about, which ones it is
 * compared with, and what the comparison request carries.
 *
 * The BESS page lets the user select several batteries. The active one is the
 * battery of the study: the study runs with it alone, and its reports describe
 * it. The others are the alternatives the Simulation page compares with it.
 * A project with a single battery (every project before this feature) is
 * unchanged by everything here.
 */
import { normalizeBtm } from "./btm";
import { isVirtualNetBilling } from "./virtualNetBilling";

/** A virtual net billing study: compared like any other, without the battery-only runs. */
export function comparesVirtualNetBilling(config) {
  return isVirtualNetBilling(normalizeBtm(config));
}

/** Up to four batteries in a comparison: the study battery and three others. */
export const MAX_COMPARED_ALTERNATIVES = 3;

/** Operating settings an alternative inherits from the study battery. */
export const OPERATING_KEYS = ["iniSoC", "startDaySoC", "dailyCycles", "minIdleMins"];

export function batteryEntryName(entry) {
  const name = String(entry?.name ?? "").trim();
  if (name) return name;
  const key = String(entry?._key ?? "");
  return key.includes(":") ? key.split(":").slice(1).join(":").trim() : key.trim();
}

function selectedEntries(config) {
  const selected = config?.bess?.selected;
  return Array.isArray(selected) ? selected.filter((item) => item && typeof item === "object") : [];
}

/** The battery the study is about: the active one, else the first selected. */
export function studyBatteryName(config) {
  const selected = selectedEntries(config);
  const active = String(config?.bess?.activeName ?? "").trim();
  if (active && selected.some((item) => batteryEntryName(item) === active)) return active;
  return selected.length ? batteryEntryName(selected[0]) : "";
}

export function studyBatteryEntry(config) {
  const name = studyBatteryName(config);
  return selectedEntries(config).find((item) => batteryEntryName(item) === name) || null;
}

/**
 * The BESS block as it describes the study: with several batteries selected,
 * only the study battery. Used for the run fingerprint, so selecting batteries
 * to compare does not make the study's reports look out of date. One selected
 * battery returns the block itself, so existing fingerprints do not move.
 */
export function studyBessBlock(bess) {
  const selected = Array.isArray(bess?.selected) ? bess.selected : [];
  if (selected.length <= 1) return bess || {};
  const entry = studyBatteryEntry({ bess });
  return entry ? { ...bess, selected: [entry] } : bess;
}

/** The alternatives: every other selected battery, and which of them are in. */
export function comparisonAlternatives(config) {
  const study = studyBatteryName(config);
  const others = selectedEntries(config).filter((item) => batteryEntryName(item) !== study);
  const all = others.map(batteryEntryName).filter(Boolean);
  const include = config?.batteryComparison?.include;
  const chosen = Array.isArray(include) ? all.filter((name) => include.includes(name)) : all;
  const counts = Object.fromEntries(others.map((item) => [batteryEntryName(item), Number(item.count) || 1]));
  return { all, selected: chosen.slice(0, MAX_COMPARED_ALTERNATIVES), counts };
}

const positiveOrNull = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : null;
};

/** The body of the check and start requests. */
export function comparisonRequest(config) {
  const settings = config?.batteryComparison || {};
  const { selected } = comparisonAlternatives(config);
  const { batteryComparison, ...rest } = config || {};
  return {
    config: rest,
    alternatives: selected.map((name) => {
      const count = positiveOrNull(settings.counts?.[name]);
      return {
        name,
        count: count && count >= 1 ? Math.round(count) : null,
        price_eur: positiveOrNull(settings.prices?.[name]),
      };
    }),
    // Virtual net billing: the battery is part of the station and may not charge
    // from the grid (ΚΥΑ art. 8), so it is never compared on its own.
    battery_only: Boolean(settings.batteryOnly) && !comparesVirtualNetBilling(config),
  };
}

/**
 * Make a compared battery the study battery, exactly as it was compared: its
 * pack count, the previous study battery's operating settings, and the price
 * entered for it (or the catalogue price when none was entered).
 */
export function asStudyBattery(config, name, { count = null, price = null } = {}) {
  const previous = studyBatteryEntry(config);
  const operating = Object.fromEntries(OPERATING_KEYS
    .filter((key) => previous?.overrides && key in previous.overrides)
    .map((key) => [key, previous.overrides[key]]));
  const selected = selectedEntries(config).map((item) => (batteryEntryName(item) === name
    ? { ...item, count: Number(count) >= 1 ? Math.round(Number(count)) : item.count,
        overrides: { ...(item.overrides || {}), ...operating } }
    : item));
  const economics = config?.feasibility?.economics || {};
  const entered = positiveOrNull(price);
  return {
    ...config,
    bess: { ...(config?.bess || {}), selected, activeName: name },
    feasibility: {
      ...(config?.feasibility || {}),
      economics: {
        ...economics,
        equipmentCostOverride: {
          ...(economics.equipmentCostOverride || {}),
          battery: entered === null ? "" : entered,
          batteryName: entered === null ? "" : name,
        },
      },
    },
  };
}
